import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { decodeSelection, updateSelectionUrl } from '../utils'

export const useSelectionParams = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()

  const { talents, careers } = useMemo(() => {
    const selection = searchParams.get('selection')
    return decodeSelection(selection)
  }, [searchParams])

  const selectionRef = useRef({ talents, careers })

  useEffect(() => {
    selectionRef.current = { talents, careers }
  }, [talents, careers])

  console.log('useSelectionParams - talents:', talents, 'careers:', careers)

  const updateSelection = useCallback(
    (newTalents, newCareers, state) => {
      updateSelectionUrl(newTalents, newCareers, navigate, location, state)
    },
    [navigate, location]
  )

  const addTalent = useCallback(
    (talentId) => {
      const { talents: currentTalents, careers: currentCareers } =
        selectionRef.current
      const newTalents = [...new Set([...currentTalents, talentId])]
      updateSelection(newTalents, currentCareers)
    },
    [updateSelection]
  )

  const addTalents = useCallback(
    (newTalents) => {
      const updatedTalents = [...new Set([...talents, ...newTalents])]
      updateSelection(updatedTalents, careers)
    },
    [talents, careers, updateSelection]
  )

  const removeTalent = useCallback(
    (talentId) => {
      const newTalents = talents.filter((id) => id !== talentId)
      updateSelection(newTalents, careers)
    },
    [talents, careers, updateSelection]
  )

  const replaceTalent = useCallback(
    (talentId) => {
      updateSelection([talentId], careers)
    },
    [careers, updateSelection]
  )

  const addCareer = useCallback(
    (careerId, state) => {
      const newCareers = [...careers, careerId]
      updateSelection(talents, newCareers, state)
    },
    [talents, careers, updateSelection]
  )

  const addCareers = useCallback(
    (newCareers) => {
      const updatedCareers = [...new Set([...careers, ...newCareers])]
      updateSelection(talents, updatedCareers)
    },
    [talents, careers, updateSelection]
  )

  const replaceCareer = useCallback(
    (careerId) => {
      updateSelection(talents, [careerId])
    },
    [talents, updateSelection]
  )

  const removeCareer = useCallback(
    (careerId) => {
      const newCareers = careers.filter((id) => id !== careerId)
      updateSelection(talents, newCareers)
    },
    [talents, careers, updateSelection]
  )

  return {
    talents,
    careers,
    updateSelection,
    addTalent,
    addTalents,
    removeTalent,
    replaceTalent,
    addCareer,
    addCareers,
    replaceCareer,
    removeCareer
  }
}
